import Fuse from "fuse.js";
import { getPhoneNumber } from "./clientUtils";

export const createSearchIndex = (clients) => {
  // Prepare clients with formatted phone numbers for searching
  const clientsWithFormattedPhones = clients.map((client) => ({
    ...client,
    full_name: `${client.first_name} ${client.last_name}`,
    formatted_phone: getPhoneNumber(client),
  }));

  const options = {
    keys: [
      // full_name is weighted well above phone so name matches win when a query
      // could plausibly hit both fields.
      { name: "full_name", weight: 0.8 },
      { name: "formatted_phone", weight: 0.2 },
      // Email is intentionally excluded from search: matching on email produced
      // noisy, low-relevance results, so search is scoped to name + phone. Left
      // commented (not deleted) in case we want to reintroduce weighted email
      // search later.
      // { name: "email", weight: 0.1 },
    ],
    // Strict threshold + ignoreLocation keeps matches tight (near-exact) while
    // still allowing a query to match anywhere in the field — so last-name-only
    // searches against "first last" aren't penalized for their position.
    threshold: 0.1,
    ignoreLocation: true,
    shouldSort: true,
  };

  return new Fuse(clientsWithFormattedPhones, options);
};

export const searchClients = (fuse, query) => {
  if (!query.trim()) {
    return fuse.getIndex().docs; // Return all clients when no query
  }

  // Strip non-digits so a phone query matches the stored format. formatted_phone
  // is stored as raw digits (e.g. "2125551234"), so search the raw-digit query
  // directly — reformatting it with punctuation would add noise that the strict
  // threshold rejects.
  const cleanQuery = query.replace(/[^\d]/g, "");
  if (cleanQuery.length >= 6 && /^\d+$/.test(cleanQuery)) {
    const results = fuse.search(cleanQuery);
    if (results.length > 0) {
      return results.map((result) => result.item);
    }
  }

  // If not a phone number or no results found, search with original query
  const results = fuse.search(query);
  return results.map((result) => result.item);
};

// Resolve the comparable value for a given sort field. Most columns read the
// client field directly, but a couple need a derived value: `phone` is split
// across area_code/phone_number (there is no `phone` field on the row), and
// `last_appointment` must compare chronologically rather than lexically.
const getSortValue = (client, field) => {
  switch (field) {
    case "phone":
      // Concatenate whatever parts are present (phones can be partial); a
      // fully-empty result is collapsed to blank (and sorted last) by
      // normalizeSortValue. Mirrors the partial number the table renders.
      return `${client.area_code ?? ""}${client.phone_number ?? ""}`;
    case "last_appointment": {
      // Coerce missing or unparseable dates to null so they sort last like
      // other blanks — getTime() returns NaN for an invalid date string, and
      // NaN would otherwise slip past the numeric branch of the comparator.
      const timestamp = client.last_appointment
        ? new Date(client.last_appointment).getTime()
        : NaN;
      return Number.isNaN(timestamp) ? null : timestamp;
    }
    default:
      return client[field];
  }
};

// Normalize a raw value for comparison: blanks (null/undefined/""/"-") collapse
// to null so they can be forced to the end; strings are trimmed and lower-cased
// for case-insensitive ordering; numbers pass through unchanged.
const normalizeSortValue = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return value;
  const trimmed = String(value).trim();
  if (trimmed === "" || trimmed === "-") return null;
  return trimmed.toLowerCase();
};

// Compare two already-normalized values. Blanks (null) always sort last,
// regardless of direction. Numbers compare numerically; strings compare
// case-insensitively and stably via localeCompare.
const compareSortValues = (a, b) => {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, {
    sensitivity: "base",
    numeric: true,
  });
};

// Ties break by name so equal primary values keep a predictable
// first-name-then-last-name order.
const NAME_TIEBREAKERS = ["first_name", "last_name"];

export const applySorting = (data, sorting) => {
  if (!sorting || !sorting.field) return data;
  const { field, direction } = sorting;
  const directionMultiplier = direction === "desc" ? -1 : 1;
  const tiebreakers = NAME_TIEBREAKERS.filter((tb) => tb !== field);

  return [...data].sort((a, b) => {
    const aValue = normalizeSortValue(getSortValue(a, field));
    const bValue = normalizeSortValue(getSortValue(b, field));

    if (aValue === null || bValue === null) {
      // Force blanks to the end regardless of the requested direction.
      const blankComparison = compareSortValues(aValue, bValue);
      if (blankComparison !== 0) return blankComparison;
    } else {
      const primaryComparison =
        compareSortValues(aValue, bValue) * directionMultiplier;
      if (primaryComparison !== 0) return primaryComparison;
    }

    // Secondary sort: always ascending by name for a stable, readable order.
    for (const tiebreaker of tiebreakers) {
      const tieComparison = compareSortValues(
        normalizeSortValue(getSortValue(a, tiebreaker)),
        normalizeSortValue(getSortValue(b, tiebreaker)),
      );
      if (tieComparison !== 0) return tieComparison;
    }

    return 0;
  });
};

export const filterClients = (
  clients,
  {
    genderFilter,
    ageGroupFilter,
    getAge,
    getAgeGroup,
    isOptedInReminder,
    isOptedInMarketing,
  },
) => {
  let results = clients;

  // Apply gender filter
  if (genderFilter) {
    results = results.filter((client) => client.gender === genderFilter);
  }

  // Apply age filter
  if (ageGroupFilter) {
    results = results.filter((client) => {
      const age = getAge(client.birthday);
      return getAgeGroup(age) === ageGroupFilter;
    });
  }

  // Apply opt-in filters
  if (isOptedInReminder) {
    results = results.filter((client) => client.is_opted_in_reminder);
  }

  if (isOptedInMarketing) {
    results = results.filter((client) => client.is_opted_in_marketing);
  }

  return results;
};

export const processClientData = ({
  clients,
  searchIndex,
  searchQuery,
  filters,
  sorting,
}) => {
  let result = clients;

  // Apply search if query exists
  if (searchQuery) {
    result = searchClients(searchIndex, searchQuery);
  }

  // Apply filters
  if (filters) {
    result = filterClients(result, filters);
  }

  // Apply sorting
  return applySorting(result, sorting);
};
